import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Typography, 
  Button, 
  Container, 
  Box, 
  Paper, 
  Divider,
  useTheme,
  alpha
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { motion } from 'framer-motion';
import logo from '../../assets/images/logo.jpeg';
import AuthService from '../../services/AuthService';

const NotFound = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  // Check if token is valid on component mount
  useEffect(() => {
    if (!AuthService.isAuthenticated()) {
      console.log('User is not authenticated, redirecting to login');
      AuthService.logout(); // This will clear tokens and redirect to login
    }
  }, []);

  return (
    <Container 
      maxWidth="md" 
      sx={{ 
        mt: { xs: 4, sm: 8 },
        height: 'calc(100vh - 100px)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        py: 4
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Paper 
          elevation={3} 
          sx={{ 
            p: { xs: 3, sm: 5 }, 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            borderRadius: 2,
            background: `linear-gradient(to bottom, ${alpha(theme.palette.background.paper, 0.9)}, ${theme.palette.background.paper})`,
            boxShadow: `0 10px 40px -10px ${alpha(theme.palette.primary.main, 0.1)}`
          }}
        >
          {/* Company Logo */}
          <Box 
            sx={{ 
              mb: 4, 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%'
            }}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
            >
              <img 
                src={logo} 
                alt="Audit-AI" 
                style={{ 
                  height: '60px',
                  width: 'auto',
                  marginBottom: '8px',
                  display: 'block'
                }} 
              />
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600,
                  color: theme.palette.primary.main,
                  textAlign: 'center'
                }}
              >
                Audit-AI
              </Typography>
            </motion.div>
          </Box>
          
          <Box 
            sx={{ 
              mb: 3, 
              p: 2, 
              borderRadius: '50%', 
              backgroundColor: alpha(theme.palette.error.main, 0.1) 
            }}
          >
            <motion.div
              animate={{ 
                scale: [1, 1.1, 1],
                rotate: [0, 5, -5, 0] 
              }}
              transition={{ 
                duration: 2,
                repeat: Infinity,
                repeatType: 'loop',
                ease: 'easeInOut'
              }}
            >
              <ErrorOutlineIcon 
                sx={{ 
                  fontSize: { xs: 60, sm: 100 }, 
                  color: 'error.main' 
                }} 
              />
            </motion.div>
          </Box>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <Typography 
              variant="h2" 
              component="h1" 
              gutterBottom
              sx={{ 
                fontWeight: 700, 
                fontSize: { xs: '2rem', sm: '3rem' },
                textAlign: 'center',
                background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1
              }}
            >
              404
            </Typography>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <Typography 
              variant="h5" 
              component="h2" 
              gutterBottom
              sx={{ 
                fontWeight: 500, 
                mb: 2,
                textAlign: 'center' 
              }}
            >
              Page Not Found
            </Typography>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ 
                mb: 4, 
                textAlign: 'center',
                maxWidth: '500px'
              }}
            >
              The page you are looking for does not exist or has been moved. 
              Please check the URL or navigate to another part of the application.
            </Typography>
          </motion.div>
          
          <Divider sx={{ width: '100%', mb: 4 }} />
          
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2
            }}
          >
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button 
                variant="contained" 
                color="primary"
                size="large"
                startIcon={<DashboardIcon />}
                onClick={() => {
                  if (AuthService.isAuthenticated()) {
                    navigate('/dashboard');
                  } else {
                    // Redirect to login if token is invalid or expired
                    AuthService.logout();
                  }
                }}
                sx={{ 
                  px: 3,
                  fontWeight: 500,
                  borderRadius: 2,
                  textTransform: 'none',
                  boxShadow: theme.shadows[2],
                  minWidth: { xs: '100%', sm: '180px' }
                }}
              >
                Go to Dashboard
              </Button>
            </motion.div>
            
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button 
                variant="outlined"
                size="large"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(-1)}
                sx={{ 
                  px: 3,
                  fontWeight: 500,
                  borderRadius: 2,
                  textTransform: 'none',
                  minWidth: { xs: '100%', sm: '180px' }
                }}
              >
                Go Back
              </Button>
            </motion.div>
          </Box>
        </Paper>
      </motion.div>
    </Container>
  );
};

export default NotFound; 